const SRC = 'use-loader-limit.ts';

import { useAppSelector } from 'store/store-hooks';
import { getClipTransition, selectSbClipPlaybackParams } from 'components/app/_helpers/_app.helper';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { isLinked } from 'components/app/editor/_mediator/editor-clips/editor-clips-linking.helper';
import { PlayerPayload } from '../sb-player-video';

const EXTRA_BUFFER: Seconds = 0.3;

export const useLoaderLimit = (playerPayload: PlayerPayload, clipId?: ClipId) => {
  const { isActive, isDamping, clipData } = playerPayload;
  const { wsGetCursorTime } = useAudioTrackContext();

  const cells = useAppSelector((state) => state.editor.cells);
  const markers = useAppSelector((state) => state.editor.markers);
  const linkedClips = useAppSelector((state) => state.editor.linkedClips);
  const sbClipPlaybackMap = useAppSelector((state) => state.sb.sbClipPlaybackMap);
  const linkedGroupIndex = useAppSelector((state) => state.sb__player.linkedGroupIndex);
  const storyConfig = useAppSelector((state) => state.editor.storyConfig);

  return (): Seconds => {
    if (!sbClipPlaybackMap || !clipId || !markers || !cells || !storyConfig || !clipData) {
      console.error(`[${SRC}] getLimit`);
      return 0;
    }

    /* helpers */

    const getTransition = () => {
      const getLastClip = () => {
        const group = linkedClips[clipData.clipGroupId];
        return group?.length ? group[group.length - 1] : clipId;
      };
      const id = isLinked(clipData.clipGroupId) ? getLastClip() : clipId;
      const { cellIndex, rate } = selectSbClipPlaybackParams(id, sbClipPlaybackMap);
      return cellIndex !== markers.length
        ? getClipTransition(cells.duration[cellIndex + 1], storyConfig.transitionTime) * rate
        : 0;
    };

    const getGroupDuration = (startFrom: Index = 0) => {
      if (!isLinked(clipData.clipGroupId)) return 0;
      const group = linkedClips[clipData.clipGroupId];
      if (!group) return 0;
      return group.slice(startFrom + 1).reduce((sum, id) => {
        const { cellIndex, rate } = selectSbClipPlaybackParams(id, sbClipPlaybackMap);
        const streamDuration = cells.duration[cellIndex] * rate;
        return sum + streamDuration;
      }, 0);
    };

    const getBeforeCursorFragment = () => {
      const cursorTime = wsGetCursorTime();
      const { cellIndex, idleStart } = selectSbClipPlaybackParams(clipId, sbClipPlaybackMap);
      const cellStartTime = cellIndex === 0 ? 0 : markers[cellIndex - 1];
      const cellCursorTime = cursorTime - cellStartTime;
      return idleStart >= cellCursorTime ? idleStart : cellCursorTime;
    };

    const getLimit = (skippedTime?: Seconds): Seconds => {
      const { cellIndex, rate, idleStart } = selectSbClipPlaybackParams(clipId, sbClipPlaybackMap);
      const groupDuration = getGroupDuration(linkedGroupIndex ?? 0);
      const cellDuration = cells.duration[cellIndex];
      const transition = getTransition();
      return (cellDuration - (skippedTime ?? idleStart)) * rate + groupDuration + transition;
    };

    const getDampingLimit = () => {
      const cursorTime = wsGetCursorTime();
      const { cellIndex, rate } = selectSbClipPlaybackParams(clipId, sbClipPlaybackMap);
      const cellEndTime = markers[cellIndex];
      const cellCursorTime = cursorTime - cellEndTime;
      const transition = getClipTransition(cells.duration[cellIndex + 1], storyConfig.transitionTime);
      return (transition - cellCursorTime) * rate;
    };

    const response = (limit: Seconds) => {
      return limit + EXTRA_BUFFER;
    };

    /* response */

    switch (true) {
      case isActive:
        return response(getLimit(getBeforeCursorFragment()));
      case isDamping:
        return response(getDampingLimit());
      default:
        return response(getLimit());
    }
  };
};
